import type { Player } from './types';
import { hasPerksInPaymentGrace } from './paddle-payment-grace';

export const FREE_ACCOUNT_ADS_FEATURE_ID = 'free_account_ads';
export const MEMBERSHIP_PROMOTIONS_FEATURE_ID = 'membership_promotions';
export const AFFILIATE_ADS_FEATURE_ID = 'affiliate_ad_placements';
export const AD_FREE_CREATIVE_FEATURE_ID = 'ad_free_creative';

export type AdAccessCohort = 'legacy_ad_free' | 'new_free';
export type AdViewerSegment = 'guest' | 'new_free' | 'legacy' | 'member';

function millis(value: unknown): number | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof (value as any)?.toMillis === 'function') return (value as any).toMillis();
  if (typeof (value as any)?.toDate === 'function') return (value as any).toDate().getTime();
  return null;
}

export function hasActiveAdFreeMembership(profile: Player | null | undefined, nowMs = Date.now()) {
  if (!profile) return false;
  const status = String(profile.subscriptionStatus || '');
  const effectiveEnd = millis(profile.subscriptionCancellationEffectiveAt)
    || millis(profile.subscriptionTrialEndsAt)
    || millis(profile.membershipTrialEndsAt)
    || millis(profile.premiumUntil);
  if (profile.subscriptionCancelAtPeriodEnd && effectiveEnd && effectiveEnd > nowMs) return true;
  if (status === 'trialing') return Boolean(effectiveEnd && effectiveEnd > nowMs);
  // A paying member whose renewal was declined keeps the perks for the
  // 7-day payment grace; the server turns the flags off when it ends.
  if (hasPerksInPaymentGrace(profile)) return true;
  if (['past_due', 'paused', 'canceled', 'trial_expired', 'refunded', 'revoked'].includes(status)) return false;
  return profile.isPremium === true || profile.isLite === true;
}

/**
 * A room whose host holds an ad-free membership is ad-free for everyone in it
 * (decided 2026-09-27). The member is ad-free anyway; the perk is for the
 * people they invite. 'pending' while the host's profile loads, so a guest
 * never sees an ad start and then vanish. Auto-host rooms have no real host
 * and never qualify. 'none' is also every page outside a room.
 */
export type RoomAdStatus = 'none' | 'pending' | 'ad_free';

export function resolveRoomAdStatus(input: {
  hostId?: string | null;
  autoHostMode?: boolean;
  hostProfile?: Player | null;
  hostProfileLoading: boolean;
  nowMs?: number;
}): RoomAdStatus {
  if (!input.hostId || input.autoHostMode) return 'none';
  if (input.hostProfileLoading) return 'pending';
  return hasActiveAdFreeMembership(input.hostProfile, input.nowMs) ? 'ad_free' : 'none';
}

export function resolveAdAccess(input: {
  user: { isAnonymous?: boolean } | null;
  profile?: Player | null;
  isLoading: boolean;
  freeAccountRolloutEnabled: boolean;
  forceGuestPreview?: boolean;
  room?: RoomAdStatus;
}) {
  const access = resolveViewerAdAccess(input);
  // Only a viewer who would otherwise get ads is changed, so the reason says
  // exactly who the room spared ('room_ad_free' gets the host's credit).
  if (!access.showAds || !input.room || input.room === 'none') return access;
  if (input.room === 'pending') return { ...access, ready: false, showAds: false, reason: 'room_pending' as const };
  return { ...access, showAds: false, reason: 'room_ad_free' as const };
}

function resolveViewerAdAccess(input: Parameters<typeof resolveAdAccess>[0]) {
  if (input.isLoading) {
    return { ready: false, showAds: false, segment: 'guest' as AdViewerSegment, reason: 'loading' as const };
  }
  // Admin View as Guest is a visual simulation. It must behave like a real
  // guest even when the underlying admin account has an ad-free membership.
  if (input.forceGuestPreview) {
    return { ready: true, showAds: true, segment: 'guest' as AdViewerSegment, reason: 'guest_preview' as const };
  }
  if (!input.user || input.user.isAnonymous) {
    return { ready: true, showAds: true, segment: 'guest' as AdViewerSegment, reason: 'guest' as const };
  }
  if (hasActiveAdFreeMembership(input.profile)) {
    return { ready: true, showAds: false, segment: 'member' as AdViewerSegment, reason: 'membership' as const };
  }
  if (!input.freeAccountRolloutEnabled) {
    return { ready: true, showAds: false, segment: 'legacy' as AdViewerSegment, reason: 'rollout_off' as const };
  }
  if (input.profile?.adAccessCohort === 'legacy_ad_free' || input.profile?.legacyAdFree === true) {
    return { ready: true, showAds: false, segment: 'legacy' as AdViewerSegment, reason: 'legacy' as const };
  }
  if (input.profile?.adAccessCohort === 'new_free') {
    return { ready: true, showAds: true, segment: 'new_free' as AdViewerSegment, reason: 'new_free' as const };
  }
  return { ready: false, showAds: false, segment: 'legacy' as AdViewerSegment, reason: 'cohort_pending' as const };
}

/**
 * Whether the site-wide sale strip and the lobby sale banner stay hidden.
 * Paying members (Lite and Elite) never get a "buy now" banner pushed at them
 * (Yahav, 2026-10-11): they are the players we least want to annoy. The shop
 * still prices everything at the sale price for them, since they went there
 * on purpose. Hidden while the account loads, so a member never sees it flash.
 * Admin View as Guest still shows it, like every other guest ad.
 */
export function hidesSalePromotion(input: {
  profile?: Player | null;
  isLoading: boolean;
  forceGuestPreview?: boolean;
  nowMs?: number;
}): boolean {
  if (input.forceGuestPreview) return false;
  if (input.isLoading) return true;
  return hasActiveAdFreeMembership(input.profile, input.nowMs);
}
